import { createPortal } from "react-dom";
import moment from "moment";

import { OntarioCommonDataSetExportForm } from "@/Administration/Components/Logs/OntarioCommonDataSetExport/OntarioCommonDataSetExportForm";
import { OntarioCommonDataSetImportForm } from "@/Administration/Components/Logs/OntarioCommonDataSetExport/OntarioCommonDataSetImportForm";
import { LogEventTypeEnum } from "@/Administration/Models/Enums/LogEventTypeEnum";
import { LogItemList } from "@/Administration/Models/Log/LogItemList";
import { LogSortColumn } from "@/Administration/Models/Log/LogSortColumn";
import { ILogTabModel } from "@/Administration/Models/UI/Logs/ILogTabModel";
import { FeatureFlagControl } from "@/Common/Components/FeatureFlagControl";
import { DownloadIconButton } from "@/Common/Components/IconControls/DownloadIconButton";
import { SpanControl } from "@/Common/Components/SpanControl";
import { CommonTableHeaderComponent } from "@/Common/Components/Tables/CommonTableHeaderComponent";
import { MyleTable } from "@/Common/Components/Tables/MyleTable";
import { MyleTableColumn, MyleTableColumnOptions } from "@/Common/Components/Tables/MyleTableColumn";
import { MyleTableColumnOrderControlv2 } from "@/Common/Components/Tables/MyleTableColumnOrderControlv2";
import { SimplePagerControl } from "@/Common/Components/Tables/SimplePagerControl";
import { DateTime } from "@/Common/Helpers/DateTime";
import { LuxonDateHelper } from "@/Common/Helpers/LuxonDateHelper";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { SortDirection } from "@/Common/Models/UI/Enums";
import { ColumnConfigurationModel } from "@/Common/Models/UI/Tables/ColumnConfigurationModel";
import { ITableRenderOptions } from "@/Common/Models/UI/Tables/ITableRenderOptions";
import { MyleTableTarget } from "@/Common/Models/UI/Tables/MyleTableTarget";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { LinkButton } from "@/DesignSystem";
import { LogSearchSetting } from "./LogSearchSetting";

import styles from "./LogTab.module.scss";

interface ILogTabProp {
    model: ILogTabModel;
}

class LogTab extends React.Component<ILogTabProp, never> {
    private readonly helper: MyleHelper = new MyleHelper();
    private readonly saveOptionKey: string = "AdminLogGrid";

    public componentDidMount(): void {
        $(window).trigger("resize");

        if (!this.props.model.get("ActionTypeCache")
            || this.props.model.get("ActionTypeCache").count() === 0
            || !this.props.model.get("UsersGlobalCache")
            || this.props.model.get("UsersGlobalCache").count() === 0
        ) {
            sessionStore.dispatch(LocalOperations.AdministrationLog.GetAllLog, { firstLoad: true });
        }
        else {
            sessionStore.dispatch(LocalOperations.AdministrationLog.GetAllLog, { firstLoad: false });
        }
    }

    public componentDidUpdate(): void {
        $(window).trigger("resize");
    }

    public render(): JSX.Element {
        const columnsOptions = this.helper.loadTableColumnOptionsFromSettings(this.saveOptionKey, this.getMyleTableColumnsDefaultOptions());
        const logs = this.props.model.get("TableData");
        const tableRenderOptions = this.loadTableRenderOptions(columnsOptions);
        const cfgm: ColumnConfigurationModel[] = this.helper.castColumnConfigurationToMyleTableColumnOptions(columnsOptions.toArray());

        const tableColumns = this.getTableColumns(columnsOptions);

        const ontarioCdsExportImportLinks = cacheStore.ClinicSettings.EnableOntarioCommonDataSetExport
            ? (
                <>
                    <LinkButton
                        label="CDS Export"
                        onClick={this.openOntarioCdsExportPopup}
                        size="md"
                        underline
                        variant="brand"
                    />
                    <LinkButton
                        label="CDS Import"
                        onClick={this.openOntarioCdsImportPopup}
                        size="md"
                        underline
                        variant="brand"
                    />
                </>
            )
            : <></>;

        return (
            <div className="workspace">
                <CommonTableHeaderComponent
                    title={cacheStore.GetString("LogsLabel")}
                    searchText={this.props.model.get("LogSearchText")}
                    searchPlaceholder={cacheStore.GetString("SearchField")}
                    onSearch={text => sessionStore.dispatch(LocalOperations.AdministrationLog.SearchSettings_SearchTextChanged, { filterText: text })}
                    onRefreshClick={() => sessionStore.dispatch(LocalOperations.AdministrationLog.RefreshGrid)}
                    onSettingsClick={() => sessionStore.dispatch(LocalOperations.AdministrationLog.SearchSettings_Open)}
                    additionalRightComponent={[ontarioCdsExportImportLinks]}
                />

                <div id="posref" className="ws-scrollable">
                    {this.props.model.get("ShowLogDisplaySettings") && (
                        <LogSearchSetting
                            id="adminLogSettings"
                            Model={this.props.model}
                            className="filter-component"
                        />
                    )}
                    <div>
                        <MyleTable
                            TableRenderOptions={tableRenderOptions}
                            TableId="adminLog_table"
                            TableDataCy="log-table"
                            Columns={tableColumns}
                            DataSource={logs}
                            RowClickOperation={LocalOperations.AdministrationLog.LogPreview_Open}
                            SaveOptionskeyTarget={this.saveOptionKey}
                            ColumnReorderTarget={MyleTableTarget.Unassigned}
                            ColumnSortTarget={MyleTableTarget.Myle_AdminLog_ColumnSortClick}
                        />
                    </div>
                    <div className="clearfix" />

                </div>

                <div className="ws-footer table-pager-footer">
                    <div className="row">
                        <div className="col-lg-4 col-md-4 col-sm-4">
                            <SimplePagerControl
                                PageSize={this.props.model.get("SelectedPageSizeOption")}
                                TotalRows={this.props.model.get("TotalQueryRows")}
                                CurrentPage={this.props.model.get("CurrentPage")}
                                EventToDispatchOnPageChange={LocalOperations.AdministrationLog.CurrentPageChange}
                            />
                        </div>
                    </div>
                </div>

                {this.props.model.get("ColumnOrderingPopupOpen") && (
                    <MyleTableColumnOrderControlv2
                        TableColumns={cfgm}
                        SaveOptionskeyTarget={this.saveOptionKey}
                        ContainerId="columnSettingsPopup"
                        CloseColumOrder={LocalOperations.AdministrationLog.SearchSettings_ColumnOrderingPopup_Open}
                    />
                )}

                <FeatureFlagControl
                    flag={cacheStore.ClinicSettings.EnableOntarioCommonDataSetExport && this.props.model.get("IsOntarioCdsExportPopupOpen")}
                    activeFeature={createPortal(<OntarioCommonDataSetExportForm onClose={this.closeOntarioCdsExportPopup} />, document.body)}
                    fallbackFeature={null}
                />

                <FeatureFlagControl
                    flag={cacheStore.ClinicSettings.EnableOntarioCommonDataSetExport && this.props.model.get("IsOntarioCdsImportPopupOpen")}
                    activeFeature={createPortal(<OntarioCommonDataSetImportForm onClose={this.closeOntarioCdsImportPopup} />, document.body)}
                    fallbackFeature={null}
                />
            </div>
        );
    }

    private getMyleTableColumnsDefaultOptions(): MyleTableColumnOptions[] {
        let cols: MyleTableColumnOptions[] = [];

        let c_opt1 = new MyleTableColumnOptions();
        c_opt1.HeaderText = cacheStore.GetString("RoleLabel");
        c_opt1.FieldName = "Role";
        c_opt1.WidthPercentageDefault = 10;

        let c_opt0 = new MyleTableColumnOptions();
        c_opt0.HeaderText = cacheStore.GetString("IPLabel");
        c_opt0.FieldName = "IP";
        c_opt0.IsSortable = true;
        c_opt0.SortColumn = LogSortColumn.IP;
        c_opt0.SortDirection = SortDirection.ASC;
        c_opt0.WidthPercentageDefault = 10;

        let c_opt2 = new MyleTableColumnOptions();
        c_opt2.HeaderText = cacheStore.GetString("UserLabel");
        c_opt2.FieldName = "User";
        c_opt2.WidthPercentageDefault = 10;

        let c_opt3 = new MyleTableColumnOptions();
        c_opt3.HeaderText = cacheStore.GetString("DescriptionLabel");
        c_opt3.FieldName = "Description";
        c_opt3.WidthPercentageDefault = 15;

        let c_opt4 = new MyleTableColumnOptions();
        c_opt4.HeaderText = cacheStore.GetString("LastNameLabel");
        c_opt4.FieldName = "LastName";
        c_opt4.WidthPercentageDefault = 10;

        let c_opt5 = new MyleTableColumnOptions();
        c_opt5.HeaderText = cacheStore.GetString("FirstNameLabel");
        c_opt5.FieldName = "FirstName";
        c_opt5.WidthPercentageDefault = 10;

        let c_opt6 = new MyleTableColumnOptions();
        c_opt6.HeaderText = cacheStore.GetString("PseudonymLabel");
        c_opt6.FieldName = "Pseudonym";
        c_opt6.IsSortable = true;
        c_opt6.SortColumn = LogSortColumn.Pseudonym;
        c_opt6.SortDirection = SortDirection.ASC;
        c_opt6.WidthPercentageDefault = 5;

        let c_opt7 = new MyleTableColumnOptions();
        c_opt7.HeaderText = cacheStore.GetString("ActionLabel");
        c_opt7.FieldName = "Action";
        c_opt7.WidthPercentageDefault = 15;

        let c_opt8 = new MyleTableColumnOptions();
        c_opt8.HeaderText = cacheStore.GetString("DateLabel");
        c_opt8.FieldName = "Date";
        c_opt8.IsSortable = true;
        c_opt8.SortColumn = LogSortColumn.Date;
        c_opt8.SortDirection = SortDirection.DESC;
        c_opt8.WidthPercentageDefault = 15;

        cols.push(c_opt8);
        cols.push(c_opt7);
        cols.push(c_opt6);
        cols.push(c_opt5);
        cols.push(c_opt4);
        cols.push(c_opt3);
        cols.push(c_opt2);
        cols.push(c_opt1);
        cols.push(c_opt0);

        return cols;
    }

    private loadTableRenderOptions(columnOptions: Immutable.List<MyleTableColumnOptions>): ITableRenderOptions {
        let defaultTablePluginOptions = {
            MinColWidth: 60,
            MinTableWidth: 980,
            Cols: columnOptions.filter(x => x.IsHidden === false).map(i => i.WidthPercentage),
            FirstColumnNoDrag: false,
            LastColumnNoDrag: false,
            Autoresize: true,
            OnColumnWidthChange: null
        };

        return Immutable.fromJS(defaultTablePluginOptions);
    }

    private getTableColumns(columnsOptions: Immutable.List<MyleTableColumnOptions>): Immutable.List<MyleTableColumn> {
        return MyleTableColumn.buildList<LogItemList>(
            columnsOptions,
            column => {
                switch (column.FieldName) {
                    case "IP":
                        return {
                            renderCell: item => (
                                <div><SpanControl text={item.Model.IP} /> </div>
                            )
                        };

                    case "Role":
                        return {
                            renderCell: item => (
                                <div><SpanControl text={item.Model.Role} /> </div>
                            )
                        };

                    case "User":
                        return {
                            renderCell: item => (
                                <div><SpanControl text={item.Model.Username} /> </div>
                            )
                        };

                    case "FirstName":
                        return {
                            renderCell: item => (
                                <div> <SpanControl text={item.Model.FirstName} /> </div>
                            )
                        };

                    case "LastName":
                        return {
                            renderCell: item => (
                                <div> <SpanControl text={item.Model.LastName} /> </div>
                            )
                        };

                    case "Pseudonym":
                        return {
                            renderCell: item => (
                                <div> <SpanControl text={item.Model.Pseudonym} /> </div>
                            )
                        };

                    case "Action":
                        return {
                            renderCell: item => (
                                <div> <SpanControl text={item.Model.Action} /> </div>
                            )
                        };

                    case "Date":
                        return {
                            renderCell: item => (
                                <div> <SpanControl text={item.Model.Date ? moment(item.Model.Date).format("YYYY-MM-DD HH:mm:ss") : ""} /> </div>
                            )
                        };

                    case "Description":
                        return {
                            renderCell: item => this.renderDescriptionCell(item)
                        };
                }
            },
            false
        );
    }

    private renderDescriptionCell(item: LogItemList): JSX.Element {
        if (
            item.Model.EventType === LogEventTypeEnum.OntarioCommonDataSetExport_RequestCompleted
            && cacheStore.ClinicSettings.EnableOntarioCommonDataSetExport
        ) {
            return (
                <div className={styles.descriptionCell}>
                    <SpanControl title={item.Model.Description} text={item.Model.Description} />
                    <DownloadIconButton
                        onClick={() => this.downloadOntarioCdsExport(item.Model.Id, item.Model.Date)}
                        disabled={this.isOntarioCdsDownloadLinkExpired(item.Model.Date)}
                    />
                </div>
            );
        }
        else if (
            item.Model.EventType === LogEventTypeEnum.OntarioCommonDataSetImport_RequestFailed || item.Model.EventType === LogEventTypeEnum.OntarioCommonDataSetImport_RequestCompleted
            && cacheStore.ClinicSettings.EnableOntarioCommonDataSetExport
        ) {
            return (
                <div className={styles.descriptionCell}>
                    <SpanControl title={item.Model.Description} text={item.Model.Description} />
                    <DownloadIconButton
                        onClick={() => this.downloadOntarioCdsImportLog(item.Model.Id, item.Model.Date)}
                        disabled={this.isOntarioCdsDownloadLinkExpired(item.Model.Date)}
                    />
                </div>
            );
        }

        return <div> <SpanControl title={item.Model.Description} text={item.Model.Description} /> </div>;
    }

    private openOntarioCdsExportPopup(): void {
        sessionStore.dispatch(LocalOperations.AdministrationLog.OntarioCdsExport_Open);
    }

    private closeOntarioCdsExportPopup(): void {
        sessionStore.dispatch(LocalOperations.AdministrationLog.OntarioCdsExport_Close);
    }

    private openOntarioCdsImportPopup(): void {
        sessionStore.dispatch(LocalOperations.AdministrationLog.OntarioCdsImport_Open);
    }

    private closeOntarioCdsImportPopup(): void {
        sessionStore.dispatch(LocalOperations.AdministrationLog.OntarioCdsImport_Close);
    }

    private async downloadOntarioCdsExport(auditLogId: string, date: Date): Promise<void> {
        if (this.isOntarioCdsDownloadLinkExpired(date))
            return;

        const downloadOntarioCommonDataSetExportUrl = apiSettings.buildAppUrl(`api/downloadOntarioCommonDatasetExport/${auditLogId}`, { auth: true });
        window.open(downloadOntarioCommonDataSetExportUrl);
    }

    private async downloadOntarioCdsImportLog(auditLogId: string, date: Date): Promise<void> {
        if (this.isOntarioCdsDownloadLinkExpired(date))
            return;

        const downloadOntarioCommonDataSetImportUrl = apiSettings.buildAppUrl(`api/downloadOntarioCommonDatasetImportLog/${auditLogId}`, { auth: true });
        window.open(downloadOntarioCommonDataSetImportUrl);
    }

    private isOntarioCdsDownloadLinkExpired(date: Date): boolean {
        return LuxonDateHelper.dateDiff(date, DateTime.nowDate(), "weeks") > 4;
    }
}

export { LogTab };
